/**
 * Web Component: <gift-card-balance-checker>
 * Dedicated JavaScript module for Gift Card balance lookup
 */
class GiftCardBalanceChecker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('[data-checker-form]');
    this.result = this.querySelector('[data-checker-result]');
    this.errorBox = this.querySelector('[data-error-box]');
    this.errorText = this.querySelector('[data-error-text]');
    this.numberInput = this.querySelector('[data-card-number]');
    this.checkBtn = this.querySelector('[data-check-btn]');
    this.checkAnotherBtn = this.querySelector('[data-check-another]');
    this.resultAmount = this.querySelector('[data-result-amount]');
    this.resultNumber = this.querySelector('[data-result-number]');
    this.initialValueEl = this.querySelector('[data-initial-value]');
    this.expiresEl = this.querySelector('[data-expires-date]');
    this.statusBadgeEl = this.querySelector('[data-status-badge]');
    this.statusAnnouncer = this.querySelector('[data-status-announcer]');

    if (!this.form || !this.numberInput) return;

    this.demoValidCard = (this.dataset.demoCard || '1234567890004827').replace(/\s/g, '');
    this.demoBalance = this.dataset.demoBalance || '$125.00';
    this.msgEmpty = this.dataset.msgEmpty || 'Please enter your Gift Card Number.';
    this.msgMinLength = this.dataset.msgMinLength || 'Gift card number must be at least 4 characters.';
    this.msgInvalid = this.dataset.msgInvalid || "That gift card number doesn't look right.";
    this.msgNotFound = this.dataset.msgNotFound || "We couldn't find a gift card with that number.";
    this.msgGeneric = this.dataset.msgGeneric || "We couldn't process this right now. Please try again shortly.";

    this.initEvents();
  }

  initEvents() {
    this._onInput = () => this.handleInput();
    this._onFocus = (e) => this.handleFocus(e.currentTarget);
    this._onSubmit = (e) => this.handleSubmit(e);
    this._onReset = () => this.resetForm();

    this.numberInput?.addEventListener('input', this._onInput);
    this.numberInput?.addEventListener('focus', this._onFocus);
    this.checkBtn?.addEventListener('focus', this._onFocus);
    this.form?.addEventListener('submit', this._onSubmit);
    this.checkAnotherBtn?.addEventListener('click', this._onReset);
  }

  disconnectedCallback() {
    this.numberInput?.removeEventListener('input', this._onInput);
    this.numberInput?.removeEventListener('focus', this._onFocus);
    this.checkBtn?.removeEventListener('focus', this._onFocus);
    this.form?.removeEventListener('submit', this._onSubmit);
    this.checkAnotherBtn?.removeEventListener('click', this._onReset);
  }

  handleInput() {
    const value = this.numberInput.value.trim();
    const cleaned = value.replace(/\s+/g, '');
    if (cleaned.length <= 16) {
      this.numberInput.value = cleaned.replace(/(.{4})/g, '$1 ').trim();
    }
    if (this.errorBox && this.errorBox.classList.contains('show')) {
      this.clearError();
    }
  }

  /**
   * Centralized Accessibility Announcement Engine
   * Universally handles live region speech and ARIA attribute bindings
   * across iOS VoiceOver, Android TalkBack, NVDA, JAWS, and desktop browsers.
   */
  announceError(msg = null) {
    if (msg) {
      if (this.errorText) {
        this.errorText.textContent = msg;
      }
      if (this.errorBox) {
        this.errorBox.classList.add('show');
      }
    }

    if (!this.errorBox || !this.errorBox.classList.contains('show')) return;

    const currentMsg = (msg || this.errorText?.textContent || '').trim();
    if (!currentMsg) return;

    const errorTextId = this.errorText?.id || '';

    // 1. Unified ARIA linking for interactive controls
    [this.numberInput, this.checkBtn].forEach((el) => {
      if (el) {
        el.classList.add('invalid');
        if (el === this.numberInput) {
          el.setAttribute('aria-invalid', 'true');
        }
        if (errorTextId) {
          el.setAttribute('aria-errormessage', errorTextId);
        }
      }
    });

    // 2. Universal Speech Announcement with Zero-Width Space toggling to bypass screen reader cache deduplication
    if (this.statusAnnouncer) {
      this._announceToggle = !this._announceToggle;
      const speechText = `Error: ${currentMsg}${this._announceToggle ? '\u200B' : ''}`;
      this.statusAnnouncer.textContent = '';
      setTimeout(() => {
        if (this.statusAnnouncer) {
          this.statusAnnouncer.textContent = speechText;
        }
      }, 50);
    }
  }

  handleFocus(targetEl) {
    if (this.errorBox && this.errorBox.classList.contains('show')) {
      this.announceError();
    }
  }

  showError(msg, fields) {
    (fields || []).forEach((field) => field?.classList.add('invalid'));
    this.announceError(msg);
  }

  clearError() {
    if (this.errorText) {
      this.errorText.textContent = '';
    }
    if (this.errorBox) {
      this.errorBox.classList.remove('show');
    }
    [this.numberInput, this.checkBtn].forEach((el) => {
      if (el) {
        el.classList.remove('invalid');
        // el.removeAttribute('aria-describedby');
        el.removeAttribute('aria-errormessage');
        if (el === this.numberInput) {
          el.setAttribute('aria-invalid', 'false');
        }
      }
    });
    if (this.statusAnnouncer) {
      this.statusAnnouncer.textContent = '';
    }
  }

  announceConfirmation(msg) {
    if (this.statusAnnouncer) {
      this.statusAnnouncer.textContent = '';
      setTimeout(() => {
        this.statusAnnouncer.textContent = msg;
      }, 50);
    }
  }

  _renderDemoResult(num) {
    if (this.resultAmount) this.resultAmount.textContent = this.demoBalance;
    if (this.resultNumber) this.resultNumber.textContent = `•••• •••• •••• ${num.slice(-4)}`;
    if (this.initialValueEl) this.initialValueEl.textContent = this.demoBalance;
    if (this.expiresEl) this.expiresEl.textContent = 'Never';
    if (this.statusBadgeEl) {
      this.statusBadgeEl.textContent = 'Active';
      this.statusBadgeEl.className = 'badge-active';
    }

    if (this.form) this.form.classList.add('hide');
    if (this.result) this.result.classList.add('show');
    this.announceConfirmation(`Gift card balance checked successfully. Available balance: ${this.demoBalance}.`);
  }

  async handleSubmit(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    this.clearError();

    const rawNum = this.numberInput?.value.trim() || '';
    const num = rawNum.replace(/\s+/g, '');

    // 1. Client Validation
    if (!num) {
      this.showError(this.msgEmpty, [this.numberInput]);
      return;
    }

    if (num.length < 4) {
      this.showError(this.msgMinLength, [this.numberInput]);
      return;
    }

    const origText = this.checkBtn ? this.checkBtn.textContent : 'Check Balance';
    if (this.checkBtn) {
      this.checkBtn.disabled = true;
      this.checkBtn.textContent = 'Checking...';
    }

    try {
      const lastFour = num.slice(-4);
      const shopDomain = window.Shopify?.shop || this.dataset.shop || '';

      // Primary Endpoint: Single try/catch for fetch & response
      const response = await fetch('https://checky-api.punchy.dev/balance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          gc_code: lastFour,
          shop: shopDomain,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();

      // Single clean condition for giftCard response
      if (json && json.giftCard && json.giftCard.balance) {
        const gc = json.giftCard;
        const masked = gc.maskedCode || `•••• •••• •••• ${lastFour}`;
        const initialVal = gc.initialValue || gc.balance;
        const expires = gc.expiresOn ? new Date(gc.expiresOn).toLocaleDateString() : 'Never';
        const isEnabled = gc.enabled !== false;

        if (this.resultAmount) this.resultAmount.textContent = gc.balance;
        if (this.resultNumber) this.resultNumber.textContent = masked;
        if (this.initialValueEl) this.initialValueEl.textContent = initialVal;
        if (this.expiresEl) this.expiresEl.textContent = expires;
        if (this.statusBadgeEl) {
          this.statusBadgeEl.textContent = isEnabled ? 'Active' : 'Disabled';
          this.statusBadgeEl.className = isEnabled ? 'badge-active' : 'badge-disabled';
        }

        if (this.form) this.form.classList.add('hide');
        if (this.result) this.result.classList.add('show');
        this.announceConfirmation(`Gift card balance checked successfully. Available balance: ${gc.balance}.`);
        return;
      }

      // Fallback for Demo Valid Card
      if (num === this.demoValidCard) {
        this._renderDemoResult(num);
        return;
      }

      // Error handling from API response JSON or Not Found
      if (json && json.error) {
        this.showError(json.error, [this.numberInput]);
      } else {
        this.showError(this.msgNotFound, [this.numberInput]);
      }
    } catch (err) {
      console.error('[Gift Card Checker] Fetch Error:', err);
      if (num === this.demoValidCard) {
        this._renderDemoResult(num);
      } else {
        this.showError(this.msgGeneric, [this.numberInput]);
      }
    } finally {
      if (this.checkBtn) {
        this.checkBtn.disabled = false;
        this.checkBtn.textContent = origText;
      }
    }
  }

  resetForm() {
    if (this.result) this.result.classList.remove('show');
    if (this.form) {
      this.form.classList.remove('hide');
      this.form.reset();
    }
    this.clearError();
    if (this.statusAnnouncer) this.statusAnnouncer.textContent = '';
  }
}

if (!customElements.get('gift-card-balance-checker')) {
  customElements.define('gift-card-balance-checker', GiftCardBalanceChecker);
}
